import React, { useState } from 'react';
import { Button, Card, Col, Modal, Row, Skeleton, Tag, Typography } from 'antd';
import { DeleteIcon, EditIcon, Group, UserAvatar } from '@/components/_common';
import { FieldTimeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useDisclosure, useWindowSize } from '@/hooks';
import { useAccountReceivableContext } from '@/pages/FinancialPages/BillToReceivePage/ReleasesPage/ReleasesDetailPage';
import {
  useAccountReceivableObservationDelete,
  useAccountReceivableObservationList,
} from '@/core/domain/financial/AccountReceivableObservation/AccountReceivableObservation.hooks';
import AccountReceivableObservationModal from '../AccountReceivableObservationModal';
import { AccountReceivableObservation } from '@/core/domain/financial/AccountReceivableObservation/AccountReceivableObservation.types';
import Meta from 'antd/lib/card/Meta';
import SpaceForButton from '@/components/_common/SpaceForButtons';

import './style.less';

const AccountReceivableObservations: React.FC = () => {
  const { isMobile } = useWindowSize();
  const { accountReceivable, currentInstallment } =
    useAccountReceivableContext();

  const installment =
    accountReceivable?.installmentReceivables[currentInstallment];

  const [selectedObservation, setSelectedObservation] =
    useState<AccountReceivableObservation>();

  const [isObservationModalOpen, setObservationModalOpen] = useDisclosure(
    false,
    {
      onClose: () => {
        setSelectedObservation(undefined);
      },
    },
  );

  const { data, isLoading } = useAccountReceivableObservationList({
    installmentReceivableId: installment?.id,
  });

  const { mutateAsync: handleDelete } = useAccountReceivableObservationDelete();

  const hableDelete = async (observationId: number) => {
    Modal.confirm({
      title: 'Remover Observação',
      content: 'Tem certeza de que deseja remover esta observação?',
      okText: 'Remover',
      okType: 'danger',
      onOk: async () => await handleDelete(observationId),
    });
  };

  const openObservationModal = (observation?: AccountReceivableObservation) => {
    setSelectedObservation(observation);
    setObservationModalOpen.open();
  };

  return (
    <>
      <SpaceForButton
        justifyContent="space-between"
        style={{ marginBottom: 24 }}
      >
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Observações
        </Typography.Title>

        <Button block type="primary" onClick={() => openObservationModal()}>
          Nova observação
        </Button>
      </SpaceForButton>

      <Skeleton avatar title={false} loading={isLoading} active>
        <Row gutter={[12, 12]}>
          {data?.map((item, index) => (
            <Col span={24} key={index}>
              <Card
                style={{ borderRadius: 4 }}
                actions={
                  isMobile
                    ? [
                        <EditIcon
                          iconProps={{
                            onClick: () => openObservationModal(item),
                          }}
                        />,
                        <DeleteIcon onClick={() => hableDelete(item.id)} />,
                      ]
                    : undefined
                }
              >
                <Meta
                  className="meta-custom"
                  avatar={
                    <UserAvatar
                      picture={item.employeePhoto}
                      gender={item.employeeGender}
                    />
                  }
                  title={
                    <Row gutter={[12, 12]}>
                      <Col flex={1}>{item.employeeName}</Col>

                      {!isMobile && (
                        <Col>
                          <Tag>
                            {moment(item.creationDate).format('DD MMM. YYYY')}
                          </Tag>

                          <Tag>
                            <FieldTimeOutlined />
                            {moment(item.creationDate).format('HH:mm')}
                          </Tag>
                        </Col>
                      )}

                      {!isMobile && (
                        <>
                          <Col>
                            <EditIcon
                              iconProps={{
                                onClick: () => openObservationModal(item),
                              }}
                            />
                          </Col>

                          <Col>
                            <DeleteIcon onClick={() => hableDelete(item.id)} />
                          </Col>
                        </>
                      )}
                    </Row>
                  }
                  description={
                    <>
                      <Typography.Text>{item.observation}</Typography.Text>

                      {isMobile && (
                        <Group position="center" style={{ padding: 12 }}>
                          <Tag>
                            {moment(item.creationDate).format('DD MMM. YYYY')}
                          </Tag>

                          <Tag>
                            <FieldTimeOutlined />
                            {moment(item.creationDate).format('HH:mm')}
                          </Tag>
                        </Group>
                      )}
                    </>
                  }
                />
              </Card>
            </Col>
          ))}

          {!data?.length && (
            <Typography.Text style={{ padding: '0 24px 0 24px' }}>
              Não há observações
            </Typography.Text>
          )}
        </Row>
      </Skeleton>

      <AccountReceivableObservationModal
        installmentReceivableId={installment?.id}
        open={isObservationModalOpen}
        onCancel={setObservationModalOpen.close}
        observationData={selectedObservation}
      />
    </>
  );
};

export default AccountReceivableObservations;
